import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/foodlens-ai-ingredient-an/dggilpddmoelgefehponacgieklibncd';

const extensionScreenshots = [
  { id: 'wholefoods', name: 'Whole Foods', image: '/images/extension-wholefoods.png' },
  { id: 'amazon', name: 'Amazon', image: '/images/extension-amazon.png' },
  { id: 'walmart', name: 'Walmart', image: '/images/extension-walmart.png' },
];

const features = [
  {
    title: 'Automatic Scanning',
    description: 'No clicking required. FoodLens automatically analyzes product pages as you browse.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    title: 'Ingredient Overlay',
    description: 'See flagged ingredients highlighted directly on the product page. No need to dig through labels.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    title: '16+ Lenses, Online',
    description: 'Allergens, medical conditions, seed oils, preservatives, and more — all working seamlessly while you shop.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
  },
  {
    title: 'Works on Edge Too',
    description: 'Using Microsoft Edge? The extension works on any Chromium-based browser.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
  },
];

const steps = [
  {
    step: 1,
    title: 'Install the Extension',
    description: 'Click "Add to Chrome" and confirm the installation. Takes less than 10 seconds.',
  },
  {
    step: 2,
    title: 'Browse as Normal',
    description: 'Shop on Amazon, Walmart, or any supported site. FoodLens works in the background.',
  },
  {
    step: 3,
    title: 'See Instant Alerts',
    description: 'When a product contains flagged ingredients, you\'ll see a clear warning overlay.',
  },
];

const pricingTiers = [
  {
    name: 'Free',
    price: '$0',
    description: 'All allergen, medical, and health lenses — free forever.',
    features: [
      '15+ detection lenses',
      'Allergens: Nuts, Soy, Egg, Dairy',
      'Medical: Celiac, PKU, G6PD, Histamine',
      'Seed Oils, Preservatives, Nitrates & more',
      'Works on Amazon, Walmart, Whole Foods, Instacart',
      'Automatic scanning, 100% Ad-free',
    ],
    cta: 'Add to Chrome',
    highlighted: false,
  },
  {
    name: 'Premium',
    monthlyPrice: '$0.99',
    yearlyPrice: '$9.99',
    yearlyMonthly: '$0.83',
    description: 'Custom lenses and power-user features.',
    trialText: '14-day free trial',
    savings: 'Save 16%',
    features: [
      'All Free features',
      'Artificial Sweeteners Lens',
      'Custom Lens creation',
      '1.5x points multiplier',
      'Priority support',
    ],
    cta: 'Start Free Trial',
    highlighted: true,
  },
];

function ExtensionPage() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isYearly, setIsYearly] = useState(false);

  // Auto-rotate every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % extensionScreenshots.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Helmet>
        <title>Seed Oil Detector Chrome Extension - Scan Ingredients While You Shop | FoodLens AI</title>
        <meta name="description" content="Free seed oil detector Chrome extension. Automatically scans for seed oils, allergens, preservatives, and 15+ dietary concerns while you shop on Amazon, Walmart, and Whole Foods." />
        <meta name="keywords" content="seed oil detector, seed oil checker, chrome extension ingredient scanner, food scanner chrome, allergen detector, seed oil detector chrome extension" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-hero noise-overlay py-12 md:py-16">
        <div className="absolute top-20 left-20 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl opacity-30" />

        <div className="section relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-teal-500/10 text-teal-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Web Extension
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-tight mb-6">
                The Only Ingredient Scanner for{' '}
                <span className="text-teal-500">Online Grocery Shopping</span>
              </h1>

              <p className="text-lg sm:text-xl text-gray-500 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                Automatically scan for allergens, celiac, seed oils, and 15+ dietary concerns while you shop on Amazon, Walmart, Whole Foods, and Instacart. No clicking, no extra steps.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
                <a
                  href={CHROME_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white font-medium text-lg rounded-xl transition-all shadow-soft hover:shadow-medium"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C8.21 0 4.831 1.757 2.632 4.501l3.953 6.848A5.454 5.454 0 0 1 12 6.545h10.691A12 12 0 0 0 12 0zM1.931 5.47A11.943 11.943 0 0 0 0 12c0 6.012 4.42 10.991 10.189 11.864l3.953-6.847a5.45 5.45 0 0 1-6.865-2.29zm13.342 2.166a5.446 5.446 0 0 1 1.45 7.09l.002.001h-.002l-3.952 6.848a12.014 12.014 0 0 0 9.75-5.712zM12 16.364a4.364 4.364 0 1 1 0-8.728 4.364 4.364 0 0 1 0 8.728z"/>
                  </svg>
                  Add to Chrome — It's Free
                </a>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                Also works on Microsoft Edge and other Chromium browsers
              </p>

              {/* Supported Sites */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
                <span className="text-sm text-gray-400">Works on:</span>
                {[
                  { name: 'Amazon', logo: '/images/logos/amazon.png' },
                  { name: 'Walmart', logo: '/images/logos/walmart.png' },
                  { name: 'Whole Foods', logo: '/images/logos/wholefoods.png' },
                  { name: 'Instacart', logo: '/images/logos/instacart.png', badge: 'Beta' },
                ].map((site) => (
                  <div key={site.name} className="relative flex items-center justify-center bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm">
                    <img src={site.logo} alt={site.name} className="h-5 max-w-[70px] object-contain" />
                    {site.badge && (
                      <span className="absolute -top-2 -right-2 text-[10px] font-bold text-teal-600 bg-teal-100 px-1.5 py-0.5 rounded-full">{site.badge}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="relative flex justify-center">
              {/* Screenshot Carousel */}
              <div className="relative w-full max-w-xl">
                <div className="bg-white rounded-2xl shadow-elevated p-4 border border-teal-500/20">
                  {/* Carousel Container */}
                  <div className="relative">
                    {extensionScreenshots.map((screenshot, index) => (
                      <div
                        key={screenshot.id}
                        className={`transition-all duration-500 ease-in-out ${
                          index === activeIndex
                            ? 'opacity-100 translate-x-0'
                            : 'opacity-0 absolute inset-0 translate-x-4'
                        }`}
                      >
                        <img
                          src={screenshot.image}
                          alt={`FoodLens on ${screenshot.name}`}
                          className="w-full h-auto rounded-xl shadow-lg"
                        />
                        {/* Popup overlay */}
                        <img
                          src="/images/extension.png"
                          alt="FoodLens extension popup"
                          className="absolute -bottom-2 -right-2 sm:bottom-2 sm:right-2 max-h-[120px] sm:max-h-[160px] w-auto rounded-lg shadow-xl border-2 border-white"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Site indicator */}
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <span className="text-xs text-gray-400">Browsing:</span>
                    <span className="text-sm font-medium text-teal-600">{extensionScreenshots[activeIndex].name}</span>
                  </div>
                </div>

                {/* Carousel Indicators */}
                <div className="flex justify-center gap-2 mt-4">
                  {extensionScreenshots.map((screenshot, index) => (
                    <button
                      key={screenshot.id}
                      onClick={() => setActiveIndex(index)}
                      className={`transition-all duration-300 rounded-full ${
                        index === activeIndex
                          ? 'w-8 h-2 bg-teal-500'
                          : 'w-2 h-2 bg-teal-300 hover:bg-teal-400'
                      }`}
                      aria-label={`View ${screenshot.name}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-background py-16 md:py-20">
        <div className="section">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="section-heading">Zero-Friction Ingredient Analysis</h2>
            <p className="section-subheading mx-auto">
              The extension runs quietly in the background, only alerting you when it matters.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="card flex gap-4 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex-shrink-0 inline-flex p-3 rounded-xl bg-teal-500/10 text-teal-600 h-fit">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="font-display text-xl text-charcoal mb-2">{feature.title}</h3>
                  <p className="text-gray-500 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-surface py-16 md:py-20">
        <div className="section">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="section-heading">Simple, Transparent Pricing</h2>
            <p className="section-subheading mx-auto">
              Start free, upgrade when you need more lenses.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <span className={`text-sm font-medium ${!isYearly ? 'text-charcoal' : 'text-gray-400'}`}>
                Monthly
              </span>
              <button
                onClick={() => setIsYearly(!isYearly)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  isYearly ? 'bg-teal-500' : 'bg-gray-300'
                }`}
                aria-label="Toggle billing period"
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    isYearly ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${isYearly ? 'text-charcoal' : 'text-gray-400'}`}>
                Yearly
              </span>
              {isYearly && (
                <span className="bg-teal-100 text-teal-700 text-xs font-bold px-2 py-1 rounded-full">
                  Save 16%
                </span>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <div
                key={tier.name}
                className={`relative rounded-3xl p-8 ${
                  tier.highlighted
                    ? 'bg-teal-500 text-white ring-4 ring-teal-300'
                    : 'bg-white border-2 border-teal-100'
                } shadow-elevated animate-fade-in-up`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {tier.highlighted && tier.trialText && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-coral-500 text-white text-sm font-bold px-4 py-1 rounded-full whitespace-nowrap">
                      {tier.trialText}
                    </span>
                  </div>
                )}

                <h3 className={`font-display text-2xl ${tier.highlighted ? 'text-white' : 'text-charcoal'} mb-2`}>
                  {tier.name}
                </h3>

                <div className="flex items-baseline gap-1 mb-1">
                  <span className={`text-4xl font-bold ${tier.highlighted ? 'text-white' : 'text-charcoal'}`}>
                    {tier.price ? tier.price : (isYearly ? tier.yearlyMonthly : tier.monthlyPrice)}
                  </span>
                  <span className={tier.highlighted ? 'text-white/80' : 'text-gray-500'}>
                    /month
                  </span>
                </div>

                {tier.highlighted && isYearly && (
                  <p className={`text-sm mb-4 ${tier.highlighted ? 'text-white/80' : 'text-gray-500'}`}>
                    Billed annually at {tier.yearlyPrice}
                  </p>
                )}
                {tier.highlighted && !isYearly && (
                  <p className={`text-sm mb-4 ${tier.highlighted ? 'text-white/80' : 'text-gray-500'}`}>
                    Billed monthly
                  </p>
                )}
                {!tier.highlighted && <div className="mb-4" />}

                <p className={`mb-6 ${tier.highlighted ? 'text-white/90' : 'text-gray-500'}`}>
                  {tier.description}
                </p>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <svg
                        className={`w-5 h-5 mt-0.5 flex-shrink-0 ${tier.highlighted ? 'text-white' : 'text-teal-500'}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={tier.highlighted ? 'text-white/90' : 'text-gray-500'}>{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={CHROME_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full text-center py-3 px-6 rounded-xl font-medium transition-all ${
                    tier.highlighted
                      ? 'bg-white text-teal-600 hover:bg-teal-50'
                      : 'bg-teal-500 text-white hover:bg-teal-600'
                  }`}
                >
                  {tier.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Install */}
      <section className="bg-background py-16 md:py-20">
        <div className="section">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="section-heading">Get Started in 30 Seconds</h2>
            <p className="section-subheading mx-auto">
              Installing FoodLens is quick and completely free.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((item, index) => (
              <div
                key={item.step}
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="inline-flex w-14 h-14 rounded-full bg-teal-500 text-white text-xl font-bold items-center justify-center mb-4">
                  {item.step}
                </div>
                <h3 className="font-display text-xl text-charcoal mb-2">{item.title}</h3>
                <p className="text-gray-500">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href={CHROME_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white font-medium text-lg rounded-xl transition-all shadow-soft hover:shadow-medium"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C8.21 0 4.831 1.757 2.632 4.501l3.953 6.848A5.454 5.454 0 0 1 12 6.545h10.691A12 12 0 0 0 12 0zM1.931 5.47A11.943 11.943 0 0 0 0 12c0 6.012 4.42 10.991 10.189 11.864l3.953-6.847a5.45 5.45 0 0 1-6.865-2.29zm13.342 2.166a5.446 5.446 0 0 1 1.45 7.09l.002.001h-.002l-3.952 6.848a12.014 12.014 0 0 0 9.75-5.712zM12 16.364a4.364 4.364 0 1 1 0-8.728 4.364 4.364 0 0 1 0 8.728z"/>
              </svg>
              Add to Chrome — Free
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export default ExtensionPage;
